import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore, } from 'react';
import { createPortal } from 'react-dom';
import { cn } from "../lib/cn.js";
/** No máximo estes na tela: o quinto aviso empurra o primeiro. */
const MAX_VISIBLE = 4;
let toasts = [];
let nextId = 1;
const listeners = new Set();
const emit = () => {
    for (const listener of listeners)
        listener();
};
const push = (tone, message, options, fallbackDuration) => {
    const id = nextId;
    nextId += 1;
    toasts = [
        ...toasts,
        {
            id,
            tone,
            message,
            duration: options?.duration ?? fallbackDuration,
            ...(options?.action === undefined ? {} : { action: options.action }),
        },
    ].slice(-MAX_VISIBLE);
    emit();
    return id;
};
/** Tira um aviso da tela. */
export const dismissToast = (id) => {
    toasts = toasts.filter((toast) => toast.id !== id);
    emit();
};
/** Tira todos — ao sair da conta, e entre um teste e outro. */
export const dismissAllToasts = () => {
    toasts = [];
    emit();
};
/**
 * Avisar, de qualquer lugar — uma tela, um `onSuccess`, o cache de mutações.
 * Devolve o id, para tirar o aviso antes da hora.
 */
export const notify = {
    success: (message, options) => push('success', message, options, 4000),
    info: (message, options) => push('info', message, options, 4000),
    error: (message, options) => push('error', message, options, 8000),
};
const subscribe = (listener) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};
const TONE = {
    success: {
        icon: _jsx(CircleCheck, { "aria-hidden": "true", className: "h-4 w-4 shrink-0" }),
        className: 'text-(--success)',
    },
    error: {
        icon: _jsx(CircleAlert, { "aria-hidden": "true", className: "h-4 w-4 shrink-0" }),
        className: 'text-(--danger)',
    },
    info: {
        icon: _jsx(Info, { "aria-hidden": "true", className: "h-4 w-4 shrink-0" }),
        className: 'text-(--info)',
    },
};
const ToastCard = ({ toast }) => {
    const timer = useRef(null);
    const stop = () => {
        if (timer.current !== null)
            window.clearTimeout(timer.current);
        timer.current = null;
    };
    const start = () => {
        stop();
        timer.current = window.setTimeout(() => {
            dismissToast(toast.id);
        }, toast.duration);
    };
    useEffect(() => {
        start();
        return stop;
        // O temporizador é deste aviso: começa uma vez, e o hover pausa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const tone = TONE[toast.tone];
    return (_jsxs("div", { role: toast.tone === 'error' ? 'alert' : 'status', 
        // Parar o mouse em cima segura o aviso para ler; sair recomeça.
        onMouseEnter: stop, onMouseLeave: start, className: cn('pointer-events-auto flex items-start gap-2 rounded-(--radius-md) border border-(--border-default)', 'bg-(--surface-raised) px-3 py-2.5 text-(length:--text-sm) text-(--text-primary) shadow-(--shadow-md)', 'animate-in fade-in-0 slide-in-from-bottom-2'), children: [_jsx("span", { className: cn('mt-0.5', tone.className), children: tone.icon }), _jsx("span", { className: "min-w-0 flex-1", children: toast.message }), toast.action === undefined ? null : (_jsx("button", { type: "button", className: "shrink-0 rounded-(--radius-sm) px-1.5 font-(--weight-medium) text-(--brand-primary) hover:underline", onClick: () => {
                    toast.action?.onClick();
                    dismissToast(toast.id);
                }, children: toast.action.label })), _jsx("button", { type: "button", "aria-label": "Fechar o aviso", className: "shrink-0 rounded-(--radius-sm) p-0.5 text-(--text-muted) hover:bg-(--surface-hover) hover:text-(--text-primary)", onClick: () => {
                    dismissToast(toast.id);
                }, children: _jsx(X, { "aria-hidden": "true", className: "h-3.5 w-3.5" }) })] }));
};
const RegionContext = createContext(null);
/**
 * O canto dos avisos, montado uma vez em volta do app: os toasts, e o lugar
 * para onde os outros avisos flutuantes (`Notice`) vão.
 */
export const NotificationArea = ({ children, above = 'edge', }) => {
    const [region, setRegion] = useState(null);
    const items = useSyncExternalStore(subscribe, () => toasts, () => toasts);
    return (_jsxs(RegionContext, { value: region, children: [children, _jsx("section", { ref: setRegion, "aria-label": "Avisos", className: cn('pointer-events-none fixed right-4 z-(--z-toast) flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2', above === 'taskbar' ? 'bottom-14' : 'bottom-4'), children: items.map((toast) => (_jsx(ToastCard, { toast: toast }, toast.id))) })] }));
};
/**
 * Um aviso que fica até alguém tirar — o pedido da loja que chegou, o
 * produto que o leitor achou —, no mesmo canto dos toasts e empilhado com
 * eles. Fora de uma `NotificationArea` (teste de um componente solto), fica
 * onde foi declarado.
 */
export const Notice = ({ children }) => {
    const region = useContext(RegionContext);
    const card = _jsx("div", { className: "pointer-events-auto", children: children });
    return region === null ? card : createPortal(card, region);
};
//# sourceMappingURL=toast.js.map